import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { PAGE_IMAGES } from '../data/assets';
type Variant = 'formula' | 'tree' | 'fpc' | 'compare' | 'worksheet' | 'fieldcard';
type Page = {
  src: string;
  variant: Variant;
  alt: string;
  caption: string;
};

/**
 * "Peek inside" — cuando haya páginas reales exportadas de Gamma, pegar las
 * URLs en data/assets.ts (PAGE_IMAGES). Mientras estén vacías se dibuja un
 * esquema de la página con la estructura real del índice (sin datos técnicos).
 */
const pages: Page[] = [{
  src: PAGE_IMAGES.formula,
  variant: 'formula',
  alt: 'The Service Call Formula as 15, 45 and 90-minute timeline bars',
  caption: 'The Service Call Formula: 15-min triage, 45-min diagnosis, 90-min full service'
}, {
  src: PAGE_IMAGES.splitTree,
  variant: 'tree',
  alt: 'The Freeze-or-Harvest Split decision tree',
  caption: 'The Freeze-or-Harvest Split decision tree'
}, {
  src: PAGE_IMAGES.fpc,
  variant: 'fpc',
  alt: 'A one-page Field Procedure Card layout',
  caption: 'Field Procedure Card · FPC 17: no ice, evaporator not freezing'
}, {
  src: PAGE_IMAGES.compare,
  variant: 'compare',
  alt: 'A side-by-side comparison page',
  caption: 'Bad valve vs dirty screen: tell them apart in 2 minutes'
}, {
  src: PAGE_IMAGES.worksheet,
  variant: 'worksheet',
  alt: 'A service call report worksheet ready to fill',
  caption: 'WS-31 Service Call Report, ready to fill on site'
}, {
  src: PAGE_IMAGES.fieldCard,
  variant: 'fieldcard',
  alt: 'A pocket field card',
  caption: 'Pocket field card for the van'
}];
const Bar = ({
  w,
  tone = 'bg-pine/15'
}: {
  w: string;
  tone?: string;
}) => <span className={`block h-1.5 rounded-full ${tone}`} style={{
  width: w
}} data-id="element-152" data-mp="3seug87tgbyus" />;
function PagePreview({
  variant
}: {
  variant: Variant;
}) {
  const header = (title: string, chip: string, chipColor: string) => <div className="flex items-center justify-between bg-forest-deep px-3 py-2" data-id="element-153" data-mp="13p3hzpdh1sjj">
      <span className="truncate font-display text-[13px] uppercase text-paper" data-id="element-154" data-mp="13n0t58o225ge">{title}</span>
      <span className={`rounded px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase text-pine ${chipColor}`} data-id="element-155" data-mp="18lu0ejcr1084">
        {chip}
      </span>
    </div>;
  if (variant === 'formula') {
    const rows = [{
      l: '15 min · Triage',
      w: '30%',
      c: 'bg-sky'
    }, {
      l: '45 min · Diagnosis',
      w: '62%',
      c: 'bg-ember'
    }, {
      l: '90 min · Full service',
      w: '96%',
      c: 'bg-moss'
    }];
    return <div className="flex h-full flex-col" data-id="element-156" data-mp="pqx4duvyapbb">
        {header('The Service Call Formula', 'Vol 1 · p4', 'bg-paper')}
        <div className="flex flex-1 flex-col justify-center gap-3 px-4" data-id="element-157" data-mp="ypijth64lo7v">
          {rows.map(r => <div key={r.l} data-id="element-158" data-mp="2562w1dgt4hg8">
              <span className="font-mono text-[9px] font-semibold text-pine-soft" data-id="element-159" data-mp="254szisplrhbp">{r.l}</span>
              <span className={`mt-1 block h-3 rounded ${r.c}`} style={{
            width: r.w
          }} data-id="element-160" data-mp="2a3w46c1o7lj5" />
            </div>)}
        </div>
      </div>;
  }
  if (variant === 'tree') {
    return <div className="flex h-full flex-col" data-id="element-161" data-mp="2f9hpj67tlpn0">
        {header('Freeze-or-Harvest Split', 'Framework', 'bg-sky')}
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-3" data-id="element-162" data-mp="2ahq93oidp0al">
          <span className="rounded-full border-2 border-pine px-3 py-1 font-mono text-[9px] font-bold text-pine" data-id="element-163" data-mp="26i8ykgo3hcyk">
            Where does the cycle fail?
          </span>
          <span className="h-3 w-px bg-pine/40" data-id="element-164" data-mp="1wjji8sbuk0kf" />
          <div className="flex w-full justify-center gap-6" data-id="element-165" data-mp="1whow5aoua2er">
            <span className="rounded-full bg-sky px-3 py-1 font-mono text-[9px] font-bold text-pine" data-id="element-166" data-mp="1rjnf0vaz4d5x">FREEZE</span>
            <span className="rounded-full bg-ember px-3 py-1 font-mono text-[9px] font-bold text-pine" data-id="element-167" data-mp="1njd557k292ls">HARVEST</span>
          </div>
          <div className="mt-1 grid w-full grid-cols-4 gap-1.5" data-id="element-168" data-mp="3lxevadnt5vac">
            {[['Water', 'bg-sys-water'], ['Refrig', 'bg-sys-refrig'], ['Harvest', 'bg-sys-harvest'], ['Controls', 'bg-sys-controls']].map(([l, c]) => <span key={l} className="flex flex-col items-center gap-1" data-id="element-169" data-mp="3tydvbhzgjdy7">
                <span className={`h-2.5 w-2.5 rounded-full ${c}`} data-id="element-170" data-mp="3fy31q76rjl9c" />
                <span className="font-mono text-[7px] font-semibold text-pine-soft" data-id="element-171" data-mp="3k0q0yt5rk8gk">{l}</span>
              </span>)}
          </div>
        </div>
      </div>;
  }
  if (variant === 'fpc') {
    const sections = ['Use it when', 'Tools · Safety', 'Steps', 'What normal looks like', 'Most common mistake', 'Escalate if · Record on WS'];
    return <div className="flex h-full flex-col" data-id="element-172" data-mp="2wcymbiwr3jc4">
        <div className="h-1.5 bg-sky" data-id="element-173" data-mp="319yq1n64oa02" />
        {header('FPC 17 · No ice, not freezing', 'Refrigeration', 'bg-sky')}
        <div className="grid flex-1 grid-cols-2 gap-x-3 gap-y-2 px-3 py-2.5" data-id="element-174" data-mp="2na78jcgekbhb">
          {sections.map((s, i) => <div key={s} className={i === 2 ? 'row-span-2' : ''} data-id="element-175" data-mp="2s88rlxdtwass" data-mpi={i}>
              <span className="font-mono text-[7.5px] font-bold uppercase text-pine" data-id="element-176" data-mp="28n8hvjpddl3n">{s}</span>
              <div className="mt-1 grid gap-1" data-id="element-177" data-mp="23oxbxk273uqp">
                <Bar w="92%" data-id="element-178" data-mp="23q8fbirqb0ha" />
                <Bar w="70%" data-id="element-179" data-mp="2hoggk2vssny9" />
                {i === 2 && <>
                    <Bar w="85%" data-id="element-180" data-mp="2hmc2wt83kaks" />
                    <Bar w="60%" data-id="element-181" data-mp="2dmbqfe4k69gd" />
                  </>}
                {i === 4 && <Bar w="55%" tone="bg-signal/40" data-id="element-182" data-mp="2rmme68akn7xb" />}
              </div>
            </div>)}
        </div>
      </div>;
  }
  if (variant === 'compare') {
    return <div className="flex h-full flex-col" data-id="element-183" data-mp="2mlf6084io6q4">
        {header('Bad valve vs dirty screen', 'Compare', 'bg-paper')}
        <div className="grid flex-1 grid-cols-2" data-id="element-184" data-mp="2wdckbnqtnet2">
          {[['Bad valve', 'bg-signal/10', 'text-signal'], ['Dirty screen', 'bg-moss-soft', 'text-moss-deep']].map(([t, bg, tc]) => <div key={t} className={`flex flex-col gap-1.5 px-3 py-3 ${bg}`} data-id="element-185" data-mp="35ej2s2d16zoy">
              <span className={`font-display text-[13px] uppercase ${tc}`} data-id="element-186" data-mp="230l71v3p1yww">{t}</span>
              <Bar w="90%" data-id="element-187" data-mp="22ys34nkgtssl" />
              <Bar w="75%" data-id="element-188" data-mp="27z6d4b5mnct9" />
              <Bar w="82%" data-id="element-189" data-mp="280hgi9v5uiju" />
              <Bar w="58%" data-id="element-190" data-mp="27xli6kfehruv" />
            </div>)}
        </div>
      </div>;
  }
  if (variant === 'worksheet') {
    return <div className="flex h-full flex-col" data-id="element-191" data-mp="2gsje80duhkqf">
        {header('WS-31 · Service Call Report', 'Fillable', 'bg-ember')}
        <div className="grid flex-1 grid-cols-3 gap-x-3 gap-y-2 px-3 py-3" data-id="element-192" data-mp="2lq1d0xy5ejkp">
          {['Site', 'Equipment #', 'Date', 'Complaint', 'Split result', 'System', 'Part tested', 'Action taken', 'Signed'].map(f => <div key={f} data-id="element-193" data-mp="2vqleo1pkxq3g">
              <span className="font-mono text-[7.5px] font-bold uppercase text-pine-soft" data-id="element-194" data-mp="2vn5yswo30d0z">{f}</span>
              <span className="mt-1 block h-3 rounded-sm border border-pine/20 bg-white" data-id="element-195" data-mp="2zorib0v4fpk5" />
            </div>)}
        </div>
      </div>;
  }
  return <div className="grid-dark flex h-full flex-col justify-between bg-forest-deep px-4 py-3" data-id="element-196" data-mp="15dm97h19mkxf">
      <span className="font-mono text-[9px] font-bold uppercase tracking-widest text-sky" data-id="element-197" data-mp="xud8sdaqlwki">Field card · Vol 1</span>
      <span className="font-display text-xl uppercase leading-none text-paper" data-id="element-198" data-mp="t124aitedrvr">
        Freeze or harvest?<br data-id="element-199" data-mp="o41txvpim2yu" />
        <span className="text-ember" data-id="element-200" data-mp="o1fp43u0e1kb">Split first.</span>
      </span>
      <div className="flex gap-1.5" data-id="element-201" data-mp="k2yjxpu5f15j">
        {['bg-sys-water', 'bg-sys-refrig', 'bg-sys-harvest', 'bg-sys-controls'].map(c => <span key={c} className={`h-2 flex-1 rounded-full ${c}`} data-id="element-202" data-mp="1gq3eoblsjd3o" />)}
      </div>
    </div>;
}
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [updateArrows]);
  const scrollByCard = (direction: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('li');
    const step = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8;
    el.scrollBy({
      left: step * direction,
      behavior: 'smooth'
    });
  };
  const arrowClass = 'absolute top-[40%] z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-paper-dark bg-white text-pine shadow-card transition-[opacity,background-color,transform] duration-150 ease-out hover:bg-paper active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-ember/50 disabled:pointer-events-none disabled:opacity-0';
  return <section aria-labelledby="peek-heading" className="grid-light w-full bg-paper" data-id="element-203" data-mp="2t39z857uoihk">
      <div className="mx-auto max-w-6xl px-5 pt-14 sm:pt-20" data-id="element-204" data-mp="2xwmkq6h8g963">
        <div className="text-center" data-id="element-205" data-mp="mgccwmwlrye6">
          <p className="eyebrow text-ember-deep" data-id="element-206" data-mp="mfb12g7rj7ve">A peek inside</p>
          <h2 id="peek-heading" className="rule-underline mt-3 font-display text-[34px] uppercase leading-tight text-pine sm:text-6xl" data-id="element-207" data-mp="hhr8sce8cukg">
            Built for use at the machine
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-pine-soft" data-id="element-208" data-mp="r8dmu26atd11">
            Not 300 pages to read in bed. One-page cards, flowcharts and forms
            you open from the symptom page and use on the call — every volume
            follows the same layout.
          </p>
        </div>

        <div className="relative mt-8" data-id="element-209" data-mp="cjhes548s00f">
          <button type="button" onClick={() => scrollByCard(-1)} disabled={!canPrev} aria-label="Previous page" className={`${arrowClass} left-0 sm:-left-2`} data-id="element-210" data-mp="cgbr8ub9h66t">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-211" data-mp="1iyqp6751fvkb" />
          </button>
          <button type="button" onClick={() => scrollByCard(1)} disabled={!canNext} aria-label="Next page" className={`${arrowClass} right-0 sm:-right-2`} data-id="element-212" data-mp="373tl0cdxcfzi">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-213" data-mp="2xd8dfzg9w14g" />
          </button>
          <ul ref={trackRef} onScroll={updateArrows} className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-5 [scrollbar-width:thin]" data-id="element-214" data-mp="2tcybqyzijwve">
            {pages.map(page => <li key={page.caption} className="shrink-0 basis-[85%] snap-start sm:basis-[360px]" data-id="element-215" data-mp="2jcnwz9xtyhe4">
                <figure className="overflow-hidden rounded-2xl border border-paper-dark bg-white shadow-card" data-id="element-216" data-mp="2adu2bp428137">
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-paper" data-id="element-217" data-mp="coaw52sq8n4o">
                    {page.src ? <img src={page.src} alt={page.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top" data-id="element-218" data-mp="8nim47c16e7c" /> : <div className="absolute inset-0" role="img" aria-label={page.alt} data-id="element-219" data-mp="20aos713l61t5">
                        <PagePreview variant={page.variant} data-id="element-220" data-mp="24bh1fucgk6pf" />
                      </div>}
                  </div>
                  <figcaption className="border-t border-paper-dark px-3.5 py-3 text-[13px] font-semibold leading-snug text-pine" data-id="element-221" data-mp="17mj7gecj8ctb">
                    {page.caption}
                  </figcaption>
                </figure>
              </li>)}
          </ul>
          <p className="mono-note mt-1 text-center text-sm text-pine-soft sm:text-base" data-id="element-222" data-mp="1lkzn4usdhdnl">
            124 field cards · 38 worksheets · 6 pocket cards across the six volumes
          </p>
        </div>
      </div>
    </section>;
}